import { useEffect, useMemo, useRef, useState } from 'react'
import StaticMap, { Marker, type MapRef } from 'react-map-gl'
import { useGetParcels, useParcelsSearch } from '~/api'
import { SimpleParcelsLayer } from '~/components/common'
import { Button } from '~/components/ui'
import { useLocalStorage } from '~/hooks'
import { Listing } from '~/models'
import { calculateIdealZoom, MAP_STYLES, MAPBOX_ACCESS_TOKEN } from '~/utils'

const pinStyle = {
  // cursor: 'pointer',
  filter: 'drop-shadow(1px 1px 1px rgb(0 0 0 / 0.25))',
}

function Pin({ size = 20, type = '' }) {
  const strokeWidth = size / 6
  if (type === 'sale') {
    return (
      <svg
        className="pin"
        height={size - 2}
        width={size - 2}
        viewBox={`0 0 ${size - 2} ${size - 2}`}
        style={{ filter: 'drop-shadow(1px 1px 1px rgb(0 0 0 / 0.25))' }}>
        <polygon
          points="9,1 17,17 1,17"
          fill="#0284c7"
          stroke="#fff"
          strokeWidth={strokeWidth}
        />
      </svg>
    )
  } else if (type === 'saleLease') {
    return (
      <svg
        className="pin"
        height={size - 3}
        width={size - 3}
        viewBox={`0 0 ${size - 3} ${size - 3}`}
        style={{ filter: 'drop-shadow(1px 1px 1px rgb(0 0 0 / 0.25))' }}>
        <polygon
          points="2,2 16,2 16,16 2,16"
          fill="#4f46e5"
          stroke="#fff"
          strokeWidth={strokeWidth}
        />
      </svg>
    )
  }

  return (
    <svg
      className="pin ml-[-1px]"
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={pinStyle}>
      <circle
        stroke="#fff"
        strokeWidth={strokeWidth}
        fill="#c026d3"
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - strokeWidth}></circle>
    </svg>
  )
}

export default function ListingStaticMap({ listing }: { listing: Listing }) {
  const mapRef = useRef<MapRef>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [mapStyle, setMapStyle] = useLocalStorage<string>(
    'listingMapStyle',
    'satellite'
  )

  const { parcels: mainParcels } = useParcelsSearch({
    latitude: listing.property.latitude,
    longitude: listing.property.longitude,
  })

  const { parcels: additionalParcels } = useGetParcels({
    parcelApns: listing.property.additionalParcelApns ?? [],
    enabled: true,
    property: listing.property,
  })

  const parcels = useMemo(
    () => (mainParcels || []).concat(additionalParcels),
    [mainParcels, additionalParcels]
  )

  useEffect(() => {
    if (mapRef.current) {
      const zoom = calculateIdealZoom(mapRef.current, parcels)
      mapRef.current.setZoom(zoom)
    }
  }, [mapRef.current, parcels])

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  if (!isLoaded) {
    return null
  }

  return (
    <StaticMap
      ref={mapRef}
      initialViewState={{
        longitude: listing.location.coordinates[0],
        latitude: listing.location.coordinates[1],
        zoom: 16,
      }}
      mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      customAttribution="Associate™ by Resimplifi"
      mapStyle={MAP_STYLES[mapStyle]}
      style={{ width: '100%', height: '100%' }}>
      <Marker
        longitude={listing.location?.coordinates[0]}
        latitude={listing.location?.coordinates[1]}
        anchor="bottom">
        <span className="pointer-events-none">
          <Pin type={listing.type} />
        </span>
      </Marker>
      {parcels.length > 0 && listing.options?.hideParcelOutline !== true && (
        <SimpleParcelsLayer
          parcels={parcels}
          style={mapStyle === 'satellite' ? 'dark' : 'light'}
        />
      )}

      <Button
        variant="map"
        size="map"
        type="button"
        className="absolute right-4 top-4 overflow-hidden"
        onClick={() => {
          setMapStyle(mapStyle === 'street' ? 'satellite' : 'street')
        }}>
        <img
          className="w-full"
          src={mapStyle === 'street' ? '/satellite.png' : '/street.png'}
        />
      </Button>
    </StaticMap>
  )
}
